import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, ArrowUpDown, Download, Edit3, Loader2, Mail, Trash2, X } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { DeleteConfirmDialog } from "../../components/DeleteConfirmDialog";
import {
  bulkDeleteEnrolments,
  bulkEnrolCsv,
  createEnrolment,
  deleteEnrolment,
  listCohorts,
  listEnrolments,
  updateEnrolment,
} from "../../lib/adminApi";
import { formatDateDDMMMYYYY, isExpiredIso } from "../../lib/dates";

function StatusBadge({ status, endDate }) {
  const expired = isExpiredIso(endDate);
  if (expired) {
    return (
      <span className="inline-flex rounded-full bg-red-500/10 px-2 py-0.5 text-[10px] uppercase tracking-widest text-red-300">
        expired
      </span>
    );
  }
  const isActive = status === "active";
  return (
    <span
      className={`inline-flex rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest ${
        isActive ? "bg-gold/10 text-gold" : "bg-muted/20 text-muted"
      }`}
      data-testid="enrolment-status-badge"
    >
      {status}
    </span>
  );
}

function OnboardedBadge({ onboarded }) {
  return (
    <span
      className={`inline-flex rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest ${
        onboarded ? "bg-emerald-400/10 text-emerald-300" : "bg-muted/10 text-muted"
      }`}
      data-testid={`onboarded-${onboarded ? "yes" : "no"}`}
    >
      {onboarded ? "onboarded" : "pending"}
    </span>
  );
}


/**
 * Sortable table header cell. Renders the label + a small arrow icon showing the
 * current sort state (none / asc / desc). Clicking toggles the sort direction for
 * this column (three-click cycle: asc → desc → asc). Kept generic so the same
 * component works for text, dates and boolean columns.
 */
function SortHeader({ label, k, current, dir, onClick }) {
  const active = current === k;
  const Icon = !active ? ArrowUpDown : dir === 1 ? ArrowUp : ArrowDown;
  return (
    <th className="px-3 py-3">
      <button
        type="button"
        onClick={() => onClick(k)}
        data-testid={`enrolments-sort-${k}`}
        className={`inline-flex items-center gap-1 uppercase tracking-widest transition-colors ${
          active ? "text-gold" : "text-muted hover:text-text"
        }`}
      >
        {label}
        <Icon className={`h-3 w-3 ${active ? "opacity-100" : "opacity-40"}`} />
      </button>
    </th>
  );
}


export function AdminEnrolmentsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [cohortFilter, setCohortFilter] = useState("");
  const [cohorts, setCohorts] = useState([]);
  const [form, setForm] = useState({
    email: "", mobile: "", name: "", cohort: "", start_date: "", end_date: "",
  });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [csvResult, setCsvResult] = useState(null);
  const [bulkOpts, setBulkOpts] = useState({ cohort: "", start_date: "", end_date: "" });
  const [editRow, setEditRow] = useState(null);
  const csvInputRef = useRef(null);
  // Multi-select: Set of enrolment IDs currently ticked.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [dialog, setDialog] = useState(null);  // {mode:"single"|"bulk", row?, ids?}
  const [dialogBusy, setDialogBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [enrol, coh] = await Promise.all([listEnrolments(), listCohorts()]);
      setRows(enrol);
      setCohorts(coh);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load enrolments.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleAdd(e) {
    e.preventDefault();
    if (!form.email && !form.mobile) { toast.error("Enter email or mobile."); return; }
    setBusy(true);
    try {
      const payload = {};
      for (const k of ["email", "mobile", "name", "cohort", "start_date", "end_date"]) {
        if (form[k]?.trim?.()) payload[k] = form[k].trim();
      }
      const created = await createEnrolment(payload);
      setRows((prev) => [created, ...prev]);
      setForm({ email: "", mobile: "", name: "", cohort: "", start_date: "", end_date: "" });
      toast.success("Enrolment added.");
      if (created.cohort && !cohorts.includes(created.cohort)) {
        setCohorts((c) => [...c, created.cohort].sort());
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add enrolment.");
    } finally {
      setBusy(false);
    }
  }

  async function remove(row) {
    setDialog({ mode: "single", row });
  }

  async function confirmSingleRemove() {
    const row = dialog?.row;
    if (!row) return;
    setDialogBusy(true);
    try {
      await deleteEnrolment(row.id);
      setRows((prev) => prev.filter((r) => r.id !== row.id));
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(row.id);
        return next;
      });
      toast.success("Enrolment deleted.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete.");
    } finally {
      setDialogBusy(false);
      setDialog(null);
    }
  }

  function removeSelected() {
    if (!selectedIds.size) return;
    setDialog({ mode: "bulk", ids: [...selectedIds] });
  }

  async function confirmBulkRemove() {
    const ids = dialog?.ids || [];
    if (!ids.length) return;
    setDialogBusy(true);
    try {
      const { deleted } = await bulkDeleteEnrolments(ids);
      const removed = new Set(ids);
      setRows((prev) => prev.filter((r) => !removed.has(r.id)));
      setSelectedIds(new Set());
      toast.success(`${deleted} enrolment${deleted === 1 ? "" : "s"} deleted.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete selected.");
    } finally {
      setDialogBusy(false);
      setDialog(null);
    }
  }

  async function handleCsvFile(file) {
    if (!file) return;
    setUploading(true);
    setCsvResult(null);
    try {
      const result = await bulkEnrolCsv(file, {
        cohort: bulkOpts.cohort,
        startDate: bulkOpts.start_date,
        endDate: bulkOpts.end_date,
      });
      setCsvResult(result);
      toast.success(`Added ${result.added}, updated ${result.updated}, ${result.skipped.length} needed attention.`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "CSV upload failed.");
    } finally {
      setUploading(false);
      if (csvInputRef.current) csvInputRef.current.value = "";
    }
  }

  function downloadErrorCsv() {
    if (!csvResult?.error_csv) return;
    const binary = atob(csvResult.error_csv);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    const blob = new Blob([bytes], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "enrolment-import-errors.csv";
    a.click();
    URL.revokeObjectURL(url);
  }

  const filtered = rows.filter((r) => {
    if (cohortFilter && r.cohort !== cohortFilter) return false;
    if (!q) return true;
    const hay = `${r.email || ""} ${r.mobile_e164 || ""} ${r.name || ""} ${r.cohort || ""}`.toLowerCase();
    return hay.includes(q.toLowerCase());
  });

  // ---------- Sortable columns ----------
  // sortKey is the field to order by; sortDir is 1 (asc) or -1 (desc). null = no sort
  // (preserves the API's default `created_at DESC` order).
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState(1);

  function toggleSort(key) {
    setSortKey((prevKey) => {
      if (prevKey === key) {
        // Same header clicked → flip direction, and third click clears the sort.
        setSortDir((d) => (d === 1 ? -1 : 1));
        return key;
      }
      setSortDir(1);
      return key;
    });
  }

  // Map header key → primitive comparable value. Booleans get coerced to 0/1 so
  // `Onboarded` sorts as No < Yes. Dates use ISO strings — already lexicographically sortable.
  const SORT_ACCESSORS = {
    email: (r) => (r.email || "").toLowerCase(),
    mobile: (r) => r.mobile_e164 || "",
    name: (r) => (r.name || "").toLowerCase(),
    cohort: (r) => (r.cohort || "").toLowerCase(),
    start: (r) => r.start_date || "",
    end: (r) => r.end_date || "",
    onboarded: (r) => (r.onboarded ? 1 : 0),
    status: (r) => r.status || "",
  };

  const sortedRows = useMemo(() => {
    if (!sortKey || !SORT_ACCESSORS[sortKey]) return filtered;
    const acc = SORT_ACCESSORS[sortKey];
    // Sort a shallow copy so React doesn't see a mutated original array reference.
    return [...filtered].sort((a, b) => {
      const av = acc(a);
      const bv = acc(b);
      if (av === bv) return 0;
      return av > bv ? sortDir : -sortDir;
    });
  }, [filtered, sortKey, sortDir]);

  // ---------- CSV export ----------
  // Whatever the admin currently sees (filter + cohort + sort) is what gets exported.
  // Row keys match the visible column labels so opening the CSV in Sheets feels natural.
  function exportCsv() {
    if (!sortedRows.length) {
      toast.error("Nothing to export.");
      return;
    }
    const headers = ["Email", "Mobile", "Name", "Cohort", "Start", "End", "Onboarded", "Status", "Created at"];
    const escape = (v) => {
      const s = v == null ? "" : String(v);
      // RFC 4180: fields containing ", CR, LF or a comma must be quoted; inner quotes doubled.
      return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [
      headers.join(","),
      ...sortedRows.map((r) => [
        r.email || "",
        r.mobile_e164 || "",
        r.name || "",
        r.cohort || "",
        formatDateDDMMMYYYY(r.start_date) || "",
        formatDateDDMMMYYYY(r.end_date) || "",
        r.onboarded ? "Yes" : "No",
        r.status || "",
        r.created_at ? new Date(r.created_at).toISOString() : "",
      ].map(escape).join(",")),
    ].join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    // Filename shape: allow-list-2026-07-21.csv — includes date so re-exports don't overwrite.
    const stamp = new Date().toISOString().slice(0, 10);
    a.download = `allow-list-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${sortedRows.length} row${sortedRows.length === 1 ? "" : "s"}.`);
  }

  // Selection helpers — mirror the pattern used by Assets/Liabilities/Members tabs.
  const visibleIds = useMemo(() => sortedRows.map((r) => r.id), [sortedRows]);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
  const someVisibleSelected = visibleIds.some((id) => selectedIds.has(id)) && !allVisibleSelected;

  // Prune stale IDs whenever a row leaves the filtered set (search/cohort change or
  // successful delete) — protects admins from bulk-deleting off-screen rows.
  useEffect(() => {
    setSelectedIds((prev) => {
      const alive = new Set(rows.map((r) => r.id));
      let dirty = false;
      const next = new Set();
      for (const id of prev) {
        if (alive.has(id)) next.add(id);
        else dirty = true;
      }
      return dirty ? next : prev;
    });
  }, [rows]);

  function toggleOne(id) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function toggleAllVisible() {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        for (const id of visibleIds) next.delete(id);
      } else {
        for (const id of visibleIds) next.add(id);
      }
      return next;
    });
  }

  return (
    <div className="flex flex-col gap-8" data-testid="admin-enrolments-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-text">Allow-list</h2>
          <p className="mt-1 max-w-2xl text-sm text-muted">
            Only users on this list can complete OTP login. Every enrolment carries a
            cohort, start/end date and status. Access auto-disables past the end date.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={exportCsv}
            disabled={!rows.length}
            data-testid="enrolments-export-csv"
            className="gap-2"
          >
            <Download className="h-3.5 w-3.5" />
            Export CSV{sortedRows.length !== rows.length ? ` (${sortedRows.length})` : ""}
          </Button>
          <a
            href="/sample-enrolments.csv"
            download="sample-enrolments.csv"
            data-testid="download-sample-csv"
            className="inline-flex items-center gap-2 rounded-md border border-gold/40 bg-gold/5 px-3 py-1.5 text-xs font-medium text-gold hover:bg-gold/10"
          >
            <Download className="h-3.5 w-3.5" />
            Sample CSV
          </a>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Add single */}
        <form onSubmit={handleAdd} className="rounded-lg border border-border bg-card p-6" data-testid="add-enrolment-form">
          <h3 className="text-lg font-semibold">Add a single member</h3>
          <p className="mt-1 text-xs text-muted">At least one of email or mobile is required. Dates default to today → +1 year.</p>
          <div className="mt-4 flex flex-col gap-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="user@example.com" data-testid="enrol-email-input" />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>Mobile (E.164)</Label>
                <Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} placeholder="+919876543210" data-testid="enrol-mobile-input" />
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <Label>Name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Arjun Sharma" data-testid="enrol-name-input" />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>Cohort</Label>
                <Input list="cohort-list" value={form.cohort} onChange={(e) => setForm({ ...form, cohort: e.target.value })} placeholder="e.g. 2026-Q1" data-testid="enrol-cohort-input" />
                <datalist id="cohort-list">{cohorts.map((c) => <option key={c} value={c} />)}</datalist>
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <Label>Start date</Label>
                <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="enrol-start-input" />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>End date</Label>
                <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="enrol-end-input" />
              </div>
            </div>
            <Button type="submit" disabled={busy} data-testid="enrol-submit">
              {busy ? "Adding…" : "Add member"}
            </Button>
          </div>
        </form>

        {/* CSV bulk */}
        <div className="rounded-lg border border-border bg-card p-6" data-testid="csv-upload-panel">
          <h3 className="text-lg font-semibold">Import members via CSV</h3>
          <p className="mt-1 text-xs text-muted">
            CSV columns: <span className="font-mono text-gold">email, mobile, name, cohort, start_date, end_date</span>.
            Blank cohort/dates fall back to the defaults you set below.
          </p>
          <div className="mt-3 grid gap-2 sm:grid-cols-3">
            <Input placeholder="Default cohort" value={bulkOpts.cohort} onChange={(e) => setBulkOpts({ ...bulkOpts, cohort: e.target.value })} data-testid="bulk-cohort-input" />
            <Input type="date" value={bulkOpts.start_date} onChange={(e) => setBulkOpts({ ...bulkOpts, start_date: e.target.value })} data-testid="bulk-start-input" />
            <Input type="date" value={bulkOpts.end_date} onChange={(e) => setBulkOpts({ ...bulkOpts, end_date: e.target.value })} data-testid="bulk-end-input" />
          </div>
          <div className="mt-4 flex flex-col gap-3">
            <input
              ref={csvInputRef}
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => handleCsvFile(e.target.files?.[0])}
              className="rounded-md border border-border bg-elevated p-2 text-sm file:mr-3 file:rounded file:border-0 file:bg-gold file:px-3 file:py-1 file:text-xs file:text-on-gold hover:file:bg-gold-bright"
              data-testid="csv-input"
            />
            {uploading && <p className="text-xs text-muted">Uploading…</p>}
            {csvResult && (
              <div className="rounded-md border border-gold/30 bg-elevated p-3 text-xs" data-testid="csv-result">
                <div className="font-medium text-text">
                  Added <span className="text-gold">{csvResult.added}</span> · Updated{" "}
                  <span className="text-gold">{csvResult.updated}</span> · Needing attention{" "}
                  <span className="text-gold">{csvResult.skipped.length}</span>
                </div>
                {csvResult.skipped.length > 0 && (
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <Button size="sm" variant="outline" onClick={downloadErrorCsv} data-testid="download-error-csv" className="gap-1">
                      <Download className="h-3.5 w-3.5" /> Download error CSV
                    </Button>
                    <span className="inline-flex items-center gap-1 text-[11px] text-muted">
                      <Mail className="h-3 w-3" />
                      {csvResult.error_email_sent ? "Email sent to your admin address." : "Email delivery unavailable — download instead."}
                    </span>
                  </div>
                )}
                {csvResult.skipped.length > 0 && (
                  <ul className="mt-3 max-h-32 list-disc space-y-1 overflow-y-auto pl-4 text-muted">
                    {csvResult.skipped.slice(0, 8).map((s) => (
                      <li key={`skip-${s.row}`}>Row {s.row} — {s.reason}</li>
                    ))}
                    {csvResult.skipped.length > 8 && <li>…{csvResult.skipped.length - 8} more (download the CSV to see all)</li>}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-lg border border-border bg-card">
        <div className="flex flex-col gap-3 border-b border-border px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-3">
            <h3 className="text-lg font-semibold">All members ({rows.length})</h3>
            {cohorts.length > 0 && (
              <select
                value={cohortFilter}
                onChange={(e) => setCohortFilter(e.target.value)}
                data-testid="cohort-filter"
                className="rounded-md border border-border bg-elevated px-2 py-1 text-xs text-text"
              >
                <option value="">All cohorts</option>
                {cohorts.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            )}
          </div>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search email / mobile / name / cohort" data-testid="enrolments-search" className="w-full sm:w-80" />
        </div>

        {/* Bulk action bar — appears only when there's a selection so the layout stays
            stable when the filter changes. */}
        {selectedIds.size > 0 && (
          <div
            className="flex items-center justify-between border-b border-border bg-elevated/50 px-4 py-2.5"
            data-testid="enrolments-bulk-bar"
          >
            <div className="text-sm text-text">
              <span className="font-semibold text-gold">{selectedIds.size}</span> selected
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())} data-testid="enrolments-bulk-clear">
                Clear
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={removeSelected}
                data-testid="enrolments-bulk-delete"
              >
                <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Delete selected
              </Button>
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border text-left text-[10px] uppercase tracking-widest text-muted">
              <tr>
                <th className="w-12 px-3 py-3">
                  <input
                    type="checkbox"
                    aria-label="Select all visible"
                    checked={allVisibleSelected}
                    ref={(el) => { if (el) el.indeterminate = someVisibleSelected; }}
                    onChange={toggleAllVisible}
                    disabled={sortedRows.length === 0}
                    data-testid="enrolments-select-all"
                    className="h-4 w-4 cursor-pointer accent-gold"
                  />
                </th>
                <SortHeader label="Email"     k="email"     current={sortKey} dir={sortDir} onClick={toggleSort} />
                <SortHeader label="Mobile"    k="mobile"    current={sortKey} dir={sortDir} onClick={toggleSort} />
                <SortHeader label="Name"      k="name"      current={sortKey} dir={sortDir} onClick={toggleSort} />
                <SortHeader label="Cohort"    k="cohort"    current={sortKey} dir={sortDir} onClick={toggleSort} />
                <SortHeader label="Start"     k="start"     current={sortKey} dir={sortDir} onClick={toggleSort} />
                <SortHeader label="End"       k="end"       current={sortKey} dir={sortDir} onClick={toggleSort} />
                <SortHeader label="Onboarded" k="onboarded" current={sortKey} dir={sortDir} onClick={toggleSort} />
                <SortHeader label="Status"    k="status"    current={sortKey} dir={sortDir} onClick={toggleSort} />
                <th className="px-3 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10} className="px-4 py-10 text-center text-muted">Loading…</td></tr>
              ) : sortedRows.length === 0 ? (
                <tr><td colSpan={10} className="px-4 py-10 text-center text-muted">No enrolments match.</td></tr>
              ) : (
                sortedRows.map((r) => (
                  <tr
                    key={r.id}
                    className={`border-b border-border/70 last:border-b-0 hover:bg-elevated/60 ${
                      selectedIds.has(r.id) ? "bg-gold/5" : ""
                    }`}
                    data-testid={`enrolment-row-${r.id}`}
                  >
                    <td className="px-3 py-3">
                      <input
                        type="checkbox"
                        aria-label={`Select enrolment ${r.email || r.mobile_e164}`}
                        checked={selectedIds.has(r.id)}
                        onChange={() => toggleOne(r.id)}
                        data-testid={`enrolment-select-${r.id}`}
                        className="h-4 w-4 cursor-pointer accent-gold"
                      />
                    </td>
                    <td className="px-3 py-3 text-text">{r.email || "—"}</td>
                    <td className="px-3 py-3 font-mono text-xs text-text">{r.mobile_e164 || "—"}</td>
                    <td className="px-3 py-3 text-muted">{r.name || "—"}</td>
                    <td className="px-3 py-3 text-muted">{r.cohort || "—"}</td>
                    <td className="px-3 py-3 text-xs text-muted">{formatDateDDMMMYYYY(r.start_date)}</td>
                    <td className="px-3 py-3 text-xs text-muted">{formatDateDDMMMYYYY(r.end_date)}</td>
                    <td className="px-3 py-3"><OnboardedBadge onboarded={r.onboarded} /></td>
                    <td className="px-3 py-3"><StatusBadge status={r.status} endDate={r.end_date} /></td>
                    <td className="px-3 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="outline" onClick={() => setEditRow(r)} data-testid={`edit-${r.id}`} className="gap-1">
                          <Edit3 className="h-3 w-3" /> Edit
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => remove(r)} data-testid={`delete-${r.id}`}>
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editRow && (
        <EditEnrolmentDialog
          row={editRow}
          cohorts={cohorts}
          onClose={() => setEditRow(null)}
          onSaved={async () => { setEditRow(null); await load(); }}
        />
      )}

      <DeleteConfirmDialog
        open={dialog?.mode === "single"}
        busy={dialogBusy}
        onCancel={() => setDialog(null)}
        onConfirm={confirmSingleRemove}
        title={`Delete ${dialog?.row?.email || dialog?.row?.mobile_e164 || "enrolment"}?`}
        description="This removes the enrolment from the allow-list. The user won't be able to log in until you re-invite them."
        confirmLabel="Yes, delete"
        testId="enrolment-single-delete-dialog"
      />
      <DeleteConfirmDialog
        open={dialog?.mode === "bulk"}
        busy={dialogBusy}
        onCancel={() => setDialog(null)}
        onConfirm={confirmBulkRemove}
        title={`Delete ${dialog?.ids?.length || 0} enrolment${dialog?.ids?.length === 1 ? "" : "s"}?`}
        description="Every selected enrolment will be removed from the allow-list. Existing member accounts stay untouched — only their invitation is revoked. To prevent accidental bulk deletion, please type DELETE below to confirm."
        requireType
        confirmLabel="Yes, delete all"
        testId="enrolment-bulk-delete-dialog"
      />
    </div>
  );
}

function EditEnrolmentDialog({ row, cohorts, onClose, onSaved }) {
  const [form, setForm] = useState({
    email: row.email || "",
    mobile: row.mobile_e164 || "",
    name: row.name || "",
    cohort: row.cohort || "",
    start_date: row.start_date || "",
    end_date: row.end_date || "",
    status: row.status || "active",
  });
  const [busy, setBusy] = useState(false);

  async function onSubmit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {};
      // Only send changed fields to avoid overwriting with empty strings.
      for (const k of ["email", "mobile", "name", "cohort", "start_date", "end_date", "status"]) {
        const cur = form[k]?.trim ? form[k].trim() : form[k];
        const orig = k === "mobile" ? (row.mobile_e164 || "") : (row[k] || "");
        if (cur !== orig) payload[k] = cur;
      }
      if (Object.keys(payload).length === 0) { onClose(); return; }
      await updateEnrolment(row.id, payload);
      toast.success("Updated.");
      await onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" data-testid="edit-enrolment-dialog">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} aria-hidden />
      <div className="relative w-full max-w-2xl overflow-hidden rounded-2xl border border-gold/30 bg-card shadow-2xl">
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <div>
            <h3 className="text-lg font-semibold text-text">Edit member</h3>
            <p className="text-xs text-muted">Any field is editable. Email/mobile changes are re-normalised.</p>
          </div>
          <button onClick={onClose} className="rounded-md border border-border p-1.5 text-muted hover:border-gold hover:text-gold" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>
        <form onSubmit={onSubmit} className="grid grid-cols-1 gap-4 p-6 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <Label>Email</Label>
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="edit-email" />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Mobile (E.164)</Label>
            <Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} placeholder="+919876543210" data-testid="edit-mobile" />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="edit-name" />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Cohort</Label>
            <Input list="cohort-list-edit" value={form.cohort} onChange={(e) => setForm({ ...form, cohort: e.target.value })} data-testid="edit-cohort" />
            <datalist id="cohort-list-edit">{cohorts.map((c) => <option key={c} value={c} />)}</datalist>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Start date</Label>
            <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="edit-start" />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>End date</Label>
            <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="edit-end" />
          </div>
          <div className="flex flex-col gap-1.5 sm:col-span-2">
            <Label>Status</Label>
            <select
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
              data-testid="edit-status"
              className="rounded-md border border-border bg-elevated px-3 py-2 text-sm text-text"
            >
              <option value="active">Active</option>
              <option value="disabled">Disabled</option>
            </select>
          </div>
          <div className="flex justify-end gap-2 sm:col-span-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="edit-submit">
              {busy ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
